import React, { useEffect, useRef } from 'react';
import propTypes from 'prop-types';
import { parallax } from '_ui-core-js/libraries/parallax.js';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { inView } from '_ui-core-js/libraries/inView.js';
import { multiplier } from '_ui-core-js/utilities/multiplier.js';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { Image } from '_ui-core-js/React/components/Image.jsx';
import { EditiorialParallaxImages } from '../../partials';
import { constants } from '../../constants';
import { BREAKPOINT_DEFINITIONS } from '_ui-core-js/constants/config.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { LBX_GTM_TRACKING_VALUES } from '../../constants/analytics.js';

export function EditorialSection(props) {

  const { title, subtitle, text, onClickActions, links, items, images, footer } = props;
  const {
    MEDIA_TYPES: { IMAGE, VIDEO }
  } = constants;

  const block = 'c-editorial-section';
  const editorialContent = items;
  const ctaHeading = text;

  function handleOnClick(link) {
    const onClickAction = _.find(link.dataAttributes, { key: 'onClickAction' })
    let onClickFunction = _.noop();

    if (onClickAction?.value) {
      onClickFunction = onClickActions[onClickAction.value]
    }
    onClickFunction();
  };

  const parallaxRef = useRef();
  const inViewRef = useRef();
  const rootElRef = useRef();

  useEffect(() => {
    inViewRef.current = inView({
      root: rootElRef.current,
      selector: '[data-parallax-target]',
      threshold: 0.1,
      activeClass: 'parallax-active',
      inactiveClass: 'parallax-inactive'
    });
    inViewRef.current.init();

    setTimeout(() => {
      parallaxRef.current = parallax({
        rootElement: rootElRef.current,
        selector: '[data-parallax-target]',
        trigger: 'scroll',
        scrollRestoration: 'smoothScroll',
        minActiveBreakpoint: BREAKPOINT_DEFINITIONS.lg
      });
      parallaxRef.current.init();
    }, 0);
    return () => {
      parallaxRef.current.destroy();
      inViewRef.current.destroy();
    };
  }, []);

  return (
    <article
      className={`${block}`}
      ref={rootElRef}
      aria-labelledby='edit-h'
    >
      <section className={`${block}__content`}>
        {images &&
          <EditiorialParallaxImages {...{
            block,
            images
          }} />
        }
        <h2
          className={`${block}__heading h1`}
          id='edit-h'
          aria-label={
            subtitle + ' ' + title
          }
        >
          <div>
            {subtitle &&
              subtitle
                .toString()
                .split(' ')
                ?.map((word, index) => (
                  <CssAnimation
                    enter={{
                      name: 'long-slide-up-fade-in',
                      delay: `${multiplier(
                        index,
                        0.25
                      )}, hidden`,
                      duration: 0.5
                    }}
                    triggerEnter={'in-view-init'}
                    key={word}
                  >
                    <span className={`${block}__lead-text h3`}>
                      {word}
                    </span>
                  </CssAnimation>
                ))}
          </div>

          {title &&
            title
              .split(' ')
              ?.map((word, index) => (
                <CssAnimation
                  enter={{
                    name: 'long-slide-up-fade-in',
                    delay: `${multiplier(
                      index,
                      0.25
                    )}, hidden`,
                    duration: 0.5
                  }}
                  triggerEnter={'in-view-init'}
                  key={word}
                >
                  <span className={`${block}__heading-text h1`}>
                    {word}
                  </span>
                </CssAnimation>
              ))}
        </h2>
        {!_.isEmpty(editorialContent) && <>
          <CssAnimation
            enter={{
              name: 'short-slide-up-fade-in',
              duration: 1
            }}
            triggerEnter={'in-view-init'}
          >
            <ul>
              {editorialContent?.map(
                ({ copy, heading }, index) => (
                  <li key={index}>
                    {heading && <h3 className='h3'>{heading}</h3>}
                    {copy && <p>{copy}</p>}
                  </li>
                )
              )}
            </ul>
          </CssAnimation>
        </>}

        <footer className={`${block}__footer`}>
          <CssAnimation
            enter={{
              name: 'horizontal-left-right-reveal',
              target: 'psuedo-element-after',
              duration: 1
            }}
            triggerEnter={'in-view-init'}
          >
            <h3 className={`${block}__footer-heading`}>
              {ctaHeading}
            </h3>
          </CssAnimation>
          {!_.isEmpty(links) && <div className={`${block}__ctas`}>
            {links.map((link) => {
              const { id, label, mobileLabel, type = `primary` } = link;
              return (
                <div className={`${block}__button`} key={`${block}-${link.id}`}>
                  <Button {...{
                    id,
                    label,
                    mobileLabel,
                    gtmLabel : CORE_GTM_TRACKING_VALUES.label[link.id] || '',
                    gtmComponentName : link.id == 'login' || link.id == 'register' ? LBX_GTM_TRACKING_VALUES.componentName.lbxWhyExperience : undefined,
                    gtmComponentGroup : link.id == 'login' || link.id == 'register' ? CORE_GTM_TRACKING_VALUES.componentGroup.newCars : undefined,
                    testId: id,
                    type: type.toLowerCase(),
                    onClickCallback: () => {
                      handleOnClick(link)
                    }
                  }} />
                </div>
              )
            })}
          </div>}
        </footer>
      </section>

      <section className={`${block}__divider`} role='region'>
        {footer.mediaType === IMAGE && (
          <Image {...{
            imageUrl: footer.url
          }} />
        )}
        {footer.mediaType === VIDEO && (
          <video className='c-media__video  c-media__video--fit'
            src={`${footer.url}#t=0.01`}
            type="video/mp4"
            autoPlay
            muted
            loop
          />
        )}
      </section>
    </article>
  );
};

EditorialSection.propTypes = {
  title: propTypes.string,
  subtitle: propTypes.string,
  text: propTypes.string,
  onClickActions: propTypes.object,
  links: propTypes.array,
  items: propTypes.array,
  images: propTypes.array,
  footer: propTypes.object
}; 